import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentMembership, getUpcomingEvents, getUpcomingRegistrations, cancelEventRegistration, getCreditBalance } from '../services/api';
import Alert from '../components/common/Alert';

function Dashboard({ user }) {
  const [membership, setMembership] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadDashboardData = async () => {
      try {
        if (!isMounted) return;
        setLoading(true);
        
        // Load all data in parallel to reduce request time
        const promises = [
          getCurrentMembership().catch(() => null), // Return null if no active membership
          getUpcomingEvents(),
          getUpcomingRegistrations(),
          getCreditBalance().catch(() => ({ balance: 0 })) // Return 0 if error
        ];

        const [membershipData, eventsData, registrationsData, creditData] = await Promise.all(promises);

        if (!isMounted) return;

        setMembership(membershipData);
        setUpcomingEvents(eventsData.results || eventsData);
        setRegistrations(registrationsData.results || registrationsData);
        setCreditBalance(creditData.balance || 0);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error loading dashboard data:', error);
        setError('Failed to load dashboard data. Please try refreshing the page.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadDashboardData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleCancelRegistration = async (registration) => {
    if (!window.confirm(`Are you sure you want to cancel your registration for "${registration.event_name}"?`)) {
      return;
    }

    setCancelling(registration.id);
    setError('');
    setSuccess('');

    try {
      await cancelEventRegistration(registration.id);
      setSuccess(`Successfully cancelled registration for "${registration.event_name}"`);

      // Refresh registrations
      const registrationsData = await getUpcomingRegistrations();
      setRegistrations(registrationsData.results || registrationsData);
    } catch (err) {
      setError(err.message || 'Failed to cancel registration');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome back, {user?.first_name || 'Member'}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's an overview of your membership and upcoming events.
        </p>
      </header>
      
      <Alert type="error" message={error} onClose={() => setError('')} />
      <Alert type="success" message={success} onClose={() => setSuccess('')} />

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Credit Balance Card */}
          {creditBalance > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 shadow rounded-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-gray-800">Available Credits</h2>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="mt-3">
                <p className="text-4xl font-bold text-green-600">
                  ${Number(creditBalance).toFixed(2)}
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Your credits will be automatically applied to your next payment
                </p>
              </div>
            </div>
          )}

          {/* Membership Status Card */}
          <div className="bg-white shadow rounded-lg p-6 col-span-1 md:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Membership Status</h2>
              {membership && (
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  Active
                </div>
              )}
            </div>
            
            {membership ? (
              <div>
                <div className="space-y-2 mb-4">
                  <p className="text-gray-700">
                    <span className="font-medium">Plan:</span> {membership.membership_type.name}
                  </p>
                  {membership.membership_type.price > 0 && membership.current_period_end && membership.current_period_end !== null && (
                    <>
                      <p className="text-gray-700">
                        <span className="font-medium">Expires:</span> {new Date(membership.current_period_end + 'T00:00:00').toLocaleDateString()}
                        {membership.days_until_expiry !== null && (
                          <span className="text-gray-600"> ({membership.days_until_expiry} days remaining)</span>
                        )}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-medium">Auto-renew:</span> {membership.auto_renew ? 'Enabled' : 'Disabled'}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex space-x-2">
                  {/* Renewal Warning for paid plans */}
                  {membership.membership_type.price > 0 && membership.days_until_expiry !== null && membership.days_until_expiry < 30 && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                    >
                      Renew Now
                    </Link>
                  )}
                  
                  {/* Upgrade Option for Free Plans */}
                  {(membership.membership_type.price == 0 || membership.membership_type.price === '0' || membership.membership_type.price === 0.00) && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Upgrade to Pro
                    </Link>
                  )}
                  
                  {/* Manage Membership - Only for paid plans */}
                  {membership.membership_type.price > 0 && (
                    <Link
                      to="/membership"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Manage Plan
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-4">
                  Join our community to access exclusive content, events, and member benefits!
                </p>
                <Link
                  to="/membership"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Choose Your Plan
                </Link>
              </div>
            )}
          </div>
          
          {/* Upcoming Events Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upcoming Events</h2>
            {upcomingEvents.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {upcomingEvents.slice(0, 3).map(event => (
                  <li key={event.id} className="py-3">
                    <Link to={`/events/${event.id}`} className="block hover:bg-gray-50">
                      <p className="font-medium text-blue-600">{event.name}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(event.start_date).toLocaleDateString()}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">No upcoming events at this time.</p>
            )}
            <div className="mt-4">
              <Link to="/events" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                View all events →
              </Link>
            </div>
          </div>
          
          {/* Your Registrations Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Your Registrations</h2>
            {registrations.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {registrations.slice(0, 3).map(registration => {
                  const isFree = registration.price_paid === 0 || registration.price_paid === '0.00' || !registration.price_paid;
                  const canCancel = registration.status === 'registered' && isFree;

                  return (
                    <li key={registration.id} className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Link
                            to={`/events/${registration.event}`}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {registration.event_name}
                          </Link>
                          <p className="text-sm text-gray-600 mt-1">
                            Status: <span className="font-medium">{registration.status_display}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Registration Code: {registration.registration_code}
                          </p>
                          {registration.price_paid > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Paid: ${Number(registration.price_paid).toFixed(2)}
                            </p>
                          )}
                        </div>
                        {canCancel && (
                          <button
                            onClick={() => handleCancelRegistration(registration)}
                            disabled={cancelling === registration.id}
                            className="ml-4 text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                          >
                            {cancelling === registration.id ? 'Cancelling...' : 'Cancel Registration'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-gray-600">You have no upcoming event registrations.</p>
            )}
          </div>
        </div>
      )}
      
      {/* Quick Links Section */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Quick Links</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Link
            to="/membership"
            className="bg-white shadow rounded-lg p-5 flex flex-col items-center text-center hover:shadow-md transition-shadow duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
            <span className="font-medium">Manage Membership</span>
          </Link>
          
          <Link
            to="/events"
            className="bg-white shadow rounded-lg p-5 flex flex-col items-center text-center hover:shadow-md transition-shadow duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium">Browse Events</span>
          </Link>
          
          <Link
            to="/profile"
            className="bg-white shadow rounded-lg p-5 flex flex-col items-center text-center hover:shadow-md transition-shadow duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="font-medium">Update Profile</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;